"use client";

import { EdgenModalCreateAble } from "@/src/components/EdgenModal";
import { callEdgenModalWhenRootReadyAsync } from "@/src/components/EdgenModal/waitForEdgenModalRoot";
import { PortfolioInsightOnboardingModalContent } from "./PortfolioInsightOnboardingModalContent";
import type {
  OpenPortfolioInsightOnboardingModalParams,
  PortfolioInsightModalAction,
  PortfolioInsightModalResult,
} from "./types";

type PortfolioInsightModalResultInternal = PortfolioInsightModalResult;

/** 与聚合弹窗 `pendingAggregatedOnboardingModal` 分离，避免互相复用 Promise */
let pendingPortfolioInsightModal: Promise<PortfolioInsightModalResultInternal> | null =
  null;

/**
 * ⑦ 独立弹窗外壳：PC 600px 居中 / H5 全屏。
 * 不含 ⑥ Loading、不调用 generate（generate 仅在 `openAggregatedOnboardingModal` 内）。
 */
const PORTFOLIO_INSIGHT_MODAL_PROPS = {
  className:
    "[--modal-radius:0px] md:[--modal-radius:24px] [--modal-height:100dvh] md:[--modal-height:90vh] md:[--modal-width:600px] [--modal-width:100%]",
  contentStyle: {
    borderRadius: "var(--modal-radius)",
    width: "var(--modal-width)",
    height: "var(--modal-height)",
    overflow: "hidden" as const,
    padding: 0,
  },
  styles: {
    body: {
      height: "100%",
    },
  },
  containerStyle: {
    height: "100%",
    width: "100%",
    maxHeight: "100%",
  },
  titleStyle: {
    minHeight: 0,
  },
  showClose: false,
  maskClosable: false,
  destroyOnClose: true,
};

/**
 * ⑦ Portfolio Insight — **独立** Modal（Host `activeStep=diagnostic`）。
 *
 * - 无内置 Loading / `useOnboardingGenerate`
 * - 无 `navigateToTrackers`；关闭仅 `resolve({ action: 'view' })`
 * - 真实数据由调用方传入 `params.payload`（推荐：父级在 Loading 步完成后 generate 再传入）
 *
 * save_goal 完整流程请使用 `openAggregatedOnboardingModal`。
 */
export const openPortfolioInsightOnboardingModal = (
  params: OpenPortfolioInsightOnboardingModalParams = {},
): Promise<PortfolioInsightModalResultInternal> => {
  if (pendingPortfolioInsightModal) {
    return pendingPortfolioInsightModal;
  }

  pendingPortfolioInsightModal = callEdgenModalWhenRootReadyAsync(
    () =>
      new Promise<PortfolioInsightModalResultInternal>((resolve) => {
        let settled = false;

        const finish = (action: PortfolioInsightModalAction) => {
          if (settled) {
            return;
          }
          settled = true;
          pendingPortfolioInsightModal = null;
          EdgenModalCreateAble.end(false);
          const result = { action };
          params.onComplete?.(result);
          resolve(result);
        };

        EdgenModalCreateAble.call({
          children: (
            <PortfolioInsightOnboardingModalContent
              payload={params.payload}
              onClose={(action = "view") => {
                finish(action);
              }}
            />
          ),
          ...PORTFOLIO_INSIGHT_MODAL_PROPS,
        });
      }),
  ).catch((error) => {
    pendingPortfolioInsightModal = null;
    throw error;
  });

  return pendingPortfolioInsightModal;
};
